import React from 'react';

// Small text-badge service icons. They render at 1em (inherit .sidebar-icon
// font-size, 18px) and use currentColor so they match the other nav icons.

interface IconProps {
  className?: string;
  title?: string;
}

const badge = (text: string, fontSize: number, title?: string) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label={title}>
    {title ? <title>{title}</title> : null}
    <text
      x="12"
      y="12.5"
      dominantBaseline="central"
      textAnchor="middle"
      fontSize={fontSize}
      fontWeight={600}
      fill="currentColor"
      fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
      letterSpacing="-0.6"
    >
      {text}
    </text>
  </svg>
);

/** Documents (DMS) */
export const DmsIcon: React.FC<IconProps> = ({ title = 'Documents (DMS)' }) => badge('DMS', 12, title);

/** Client's Accounts — "C$" */
export const ClientAccountsIcon: React.FC<IconProps> = ({ title = "Client's Accounts" }) => badge('C$', 19, title);

/** Tax Filing — "Tax" */
export const TaxIcon: React.FC<IconProps> = ({ title = 'Tax Filing' }) => badge('Tax', 15, title);

/** Franchisee — "Fran" */
export const FranIcon: React.FC<IconProps> = ({ title = 'Franchisee' }) => badge('Fran', 10.5, title);

/**
 * Notes — a lined notepad with a pencil. currentColor (matches the nav states),
 * and the handwritten squiggle re-draws itself on hover / when active (the
 * `.notes-scribble` + `.notes-pencil` animation lives in ServiceTopBar.css).
 */
export const NotesIcon: React.FC<{ size?: number; className?: string; title?: string }> = ({ size, className, title = 'Your Knowledge Vault' }) => (
  <svg
    className={`notes-anim-icon ${className || ''}`}
    width={size ?? '1em'}
    height={size ?? '1em'}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label={title}
  >
    {title ? <title>{title}</title> : null}
    {/* Notepad body */}
    <rect x="2" y="3.6" width="17" height="16.8" rx="2.4" />
    {/* Spiral binding */}
    <path d="M6 2.5v2.4M10 2.5v2.4M14 2.5v2.4" />
    {/* The vault's monogram — reads "KV" (Knowledge Vault), legible even small */}
    <text
      x="10.5"
      y="12.4"
      textAnchor="middle"
      fontSize="8"
      fontWeight={700}
      letterSpacing="-0.2"
      fill="currentColor"
      stroke="none"
      fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
    >
      KV
    </text>
    {/* Handwritten underline — animated (draws itself) */}
    <path className="notes-scribble" d="M5 14.8q1.5-1.4 3 0t3 0" />
    {/* Pencil resting across the lower-right */}
    <g className="notes-pencil">
      <path d="M19 10.3 12.6 16.5" strokeWidth={2.3} />
      <path d="M12.6 16.5 10.9 18 11.6 14.9z" fill="currentColor" stroke="none" />
    </g>
  </svg>
);
